/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { Helmet } from 'react-helmet';
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { LoadScript } from "@react-google-maps/api";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { TagManager } from "./shared/utilities/tagmanager";

import PageContainer from "./pages/PageContainer/PageContainer";
import SalesApp from "./SalesApp";
import ddLog from "./shared/utilities/datadog/log";
import amplitudeAnalytics from "./shared/utilities/amplitude";
import {
  initializeAmplitudeExperiment,
  setExperimentOverrideDisabled,
} from "./shared/utilities/amplitudeExperiment";
import {
  canonicalPages,
  dayModePages,
  nightModePages,
  passwordlessNightModeScreen
} from "./shared/constants/themePages";

import { GOOGLE_LIBS } from "./shared/constants/locations";

import * as elementsActions from "./redux/actions/elements";
import * as experimentsActions from "./redux/actions/experiments";
import Loader from "./components/Loader/Loader";
import ExperimentDebugPanel from "./components/ExperimentDebugPanel/ExperimentDebugPanel";

import "./App.css";
import { removeCountryPathFromLocation } from "./shared/utilities/pathes";

const NIGHT_MODE_APP_CLASS =
  "bg-dark to-darkBlack from-dark bg-gradient-to-br dark:bg-gradient-from-tr dark:bg-gradient-to-b";
const DEFAULT_APP_CLASS =
  "dark:from-darkBlack dark:to-dark bg-light dark:bg-gradient-from-tr dark:bg-gradient-to-b";

export const getPathnameForAppTheme = pathname => {
  if (!pathname) {
    return pathname;
  }
  const withoutCountryPrefix = removeCountryPathFromLocation(pathname);
  return withoutCountryPrefix === "" ? "/" : withoutCountryPrefix;
};

export const getAppShellClassName = pathname => {
  const themePathname = getPathnameForAppTheme(pathname);
  const isNightModePage =
    nightModePages.includes(themePathname) ||
    passwordlessNightModeScreen.some(screenPath => {
      const regex = new RegExp(`^${screenPath}$`);
      return regex.test(themePathname);
    });
  const isDayModePage = dayModePages.includes(themePathname);

  if (isDayModePage) {
    return "bg-light";
  }
  return isNightModePage ? NIGHT_MODE_APP_CLASS : DEFAULT_APP_CLASS;
};

const isExperimentDisabledFromSearch = search => {
  try {
    return new URLSearchParams(search || "").get("disable_exp") === "1";
  } catch {
    return false;
  }
};

class App extends Component {
  constructor(props) {
    super(props);

    const {
      showMobileMenu,
      showHeaderWidget,
      categoriesPosition,
      isStickyCategories,
      showDeliveryPickupWidget,
      location,
    } = this.props;

    this.initialExperimentsDisabled = isExperimentDisabledFromSearch(
      location?.search,
    );
    setExperimentOverrideDisabled(this.initialExperimentsDisabled);

    this.state = {
      showMobileMenu, // eslint-disable-line
      showHeaderWidget, // eslint-disable-line
      categoriesPosition, // eslint-disable-line
      isStickyCategories, // eslint-disable-line
      showDeliveryPickupWidget, // eslint-disable-line
      isRouteChanging: false, // Track route changes
    };

    this.page = React.createRef(); // create self reference

    const scriptEnvironments = ["local", "dev", "production", "staging"];

    if (scriptEnvironments.includes(window.environment.REACT_APP_ENV)) {
      const tagManagerArgs = {
        gtmId: window.environment.REACT_APP_GOOGLE_TAG_MANAGER_ID
      };
      TagManager.initialize(tagManagerArgs);

      ddLog.initialize(window.environment);
    }

    // Amplitude: run for all environments (PR/preview often use other REACT_APP_ENV
    // values). initialize() no-ops when config is missing (direct: no key; proxy: no GraphQL URL).
    amplitudeAnalytics.initialize(window.environment);
    initializeAmplitudeExperiment(window.environment);
  }


  componentDidMount() {
    const { setExperimentsDisabled } = this.props;
    if (setExperimentsDisabled) {
      setExperimentsDisabled(this.initialExperimentsDisabled);
    }
    // set document title
    document.title = "Insomnia"; // eslint-disable-line
    const { history } = this.props;
    // route change listener
    this.unlisten = history.listen(() => {
      const { showHeaderWidget, toggleHeaderWidget } = this.props;

      // Show loader on route change
      this.setState({ isRouteChanging: true });

      // 1. header widget is rendered, then turn it off
      if (showHeaderWidget) toggleHeaderWidget();
      // 2. scroll to the top of the page on every route change
      window.scrollTo(0, 0); // eslint-disable-line

      // Hide loader after a short delay to show the transition
      setTimeout(() => {
        this.setState({ isRouteChanging: false });
      }, 300); // 300ms delay to show the loader
    });
  }

  componentWillUnmount() {
    // Clean up the history listener
    if (this.unlisten) {
      this.unlisten();
    }
  }



  render() {
    const {
      showMobileMenu,
      errorMessage,
      history,
      location,
    } = this.props;
    const { isRouteChanging } = this.state;
    // Check if this is a sales route
    const isSalesRoute = location?.pathname?.startsWith('/sales/custom-order');

    // If it's a sales route, render the SalesApp
    if (isSalesRoute) {
      return <SalesApp />;
    }
    const cssClass = getAppShellClassName(location?.pathname);

    const isCanonicalPage = canonicalPages.some(page =>
      location.pathname === page || location?.pathname?.startsWith(`${page}/`) || location.pathname === '/'
    );

    return (
      <LoadScript
        googleMapsApiKey={window.environment.REACT_APP_MAPS_API_KEY}
        libraries={GOOGLE_LIBS}
      >
        <GoogleReCaptchaProvider
          useEnterprise
          reCaptchaKey={
            window.environment.REACT_APP_ENTERPRISE_RECAPTCHA_SITE_KEY
          }
          scriptProps={{
            async: true,
            defer: true
          }}
        >
          <div className={`App ${cssClass}`}>
            <Helmet>
              {isCanonicalPage && <link type="canonical" rel="canonical" href={window.location.href} />}
            </Helmet>
            {isRouteChanging && (
              <div style={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%', 
                backgroundColor: 'rgba(255, 255, 255, 1)', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                zIndex: 9999 
              }}
              >
                <Loader />
              </div>
            )}
            <PageContainer
              showMobileMenu={showMobileMenu}
              errorMessage={errorMessage}
              history={history}
            />
            <ExperimentDebugPanel />
          </div>
        </GoogleReCaptchaProvider>
      </LoadScript>
    );
  }
}

export const mapStateToProps = state => {
  const {
    showMobileMenu,
    showHeaderWidget,
    categoriesPosition,
    isStickyCategories,
    showDeliveryPickupWidget
  } = state.elements;

  return {
    showMobileMenu,
    showHeaderWidget,
    categoriesPosition,
    isStickyCategories,
    showDeliveryPickupWidget,
    user: state.user,
  };
};

export const mapDispatchToProps = dispatch => ({
  toggleHeaderWidget: () => dispatch(elementsActions.toggleHeaderWidget()),
  setExperimentsDisabled: value =>
    dispatch(experimentsActions.setExperimentsDisabled(value))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
